import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Sparkles, 
  Clock, 
  Globe, 
  Loader2, 
  CheckCircle,
  Mail,
  Receipt
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PreferencesSettingsProps {
  merchantId: string;
}

export function PreferencesSettings({ merchantId }: PreferencesSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    currency: 'INR',
    default_deadline_hours: 72,
    auto_invoice_splits: true,
    default_tax_rate: 0,
    split_invoice_prefix: 'GV',
  });
  const [terminology, setTerminology] = useState({
    orders: '',
    products: '',
    customers: '',
    store: '',
  });

  useEffect(() => {
    fetchPreferences();
  }, [merchantId]);

  const fetchPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('currency, default_deadline_hours, auto_invoice_splits, default_tax_rate, split_invoice_prefix, professional_terminology')
        .eq('id', merchantId)
        .single();
      
      if (error) throw error;
      if (data) {
        setForm({
          currency: data.currency || 'INR',
          default_deadline_hours: data.default_deadline_hours || 72,
          auto_invoice_splits: data.auto_invoice_splits ?? true,
          default_tax_rate: data.default_tax_rate || 0,
          split_invoice_prefix: data.split_invoice_prefix || 'GV',
        });
        if (data.professional_terminology) {
          const t = data.professional_terminology as Record<string, string>;
          setTerminology({
            orders: t.orders || '',
            products: t.products || '',
            customers: t.customers || '',
            store: t.store || '',
          });
        }
      }
    } catch (err) {
      console.error('Error fetching preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanTerminology = Object.fromEntries(
        Object.entries(terminology).filter(([_, v]) => v.trim() !== '')
      );
      
      const { error } = await supabase
        .from('merchants')
        .update({
          currency: form.currency,
          default_deadline_hours: form.default_deadline_hours,
          auto_invoice_splits: form.auto_invoice_splits,
          default_tax_rate: form.default_tax_rate,
          split_invoice_prefix: form.split_invoice_prefix,
          professional_terminology: Object.keys(cleanTerminology).length > 0 ? cleanTerminology : null,
        })
        .eq('id', merchantId);
      if (error) throw error;
      toast.success('Preferences saved!');
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Preferences</h2>
        <p className="text-sm text-muted-foreground">Customize your workspace settings</p>
      </div>

      {/* General */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4 text-cyan-600" />
            General
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={v => setForm(p => ({ ...p, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">₹ INR</SelectItem>
                  <SelectItem value="USD">$ USD</SelectItem>
                  <SelectItem value="EUR">€ EUR</SelectItem>
                  <SelectItem value="GBP">£ GBP</SelectItem>
                  <SelectItem value="AED">AED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Default Payment Deadline</Label>
              <Select value={String(form.default_deadline_hours)} onValueChange={v => setForm(p => ({ ...p, default_deadline_hours: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="72">3 days</SelectItem>
                  <SelectItem value="168">7 days</SelectItem>
                  <SelectItem value="336">14 days</SelectItem>
                  <SelectItem value="720">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Tax Rate (%)</Label>
              <Input 
                type="number" 
                value={form.default_tax_rate} 
                onChange={e => setForm(p => ({ ...p, default_tax_rate: Number(e.target.value) }))} 
                min={0} max={100} step={0.5}
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice Prefix</Label>
              <Input 
                value={form.split_invoice_prefix} 
                onChange={e => setForm(p => ({ ...p, split_invoice_prefix: e.target.value.toUpperCase().slice(0, 5) }))} 
                placeholder="GV"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auto Invoice */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-cyan-600" />
              <div>
                <CardTitle className="text-base">Auto-Generate Invoices</CardTitle>
                <CardDescription>Automatically create invoices for collections</CardDescription>
              </div>
            </div>
            <Switch 
              checked={form.auto_invoice_splits} 
              onCheckedChange={v => setForm(p => ({ ...p, auto_invoice_splits: v }))} 
            />
          </div>
        </CardHeader>
      </Card>

      {/* Custom Terminology */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-600" />
            Custom Labels
          </CardTitle>
          <CardDescription>Personalize how things are named in your dashboard (leave blank for defaults)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Payments → </Label>
              <Input value={terminology.orders} onChange={e => setTerminology(p => ({ ...p, orders: e.target.value }))} placeholder="Payments" />
            </div>
            <div className="space-y-2">
              <Label>Services → </Label>
              <Input value={terminology.products} onChange={e => setTerminology(p => ({ ...p, products: e.target.value }))} placeholder="Services" />
            </div>
            <div className="space-y-2">
              <Label>Clients → </Label>
              <Input value={terminology.customers} onChange={e => setTerminology(p => ({ ...p, customers: e.target.value }))} placeholder="Clients" />
            </div>
            <div className="space-y-2">
              <Label>Business → </Label>
              <Input value={terminology.store} onChange={e => setTerminology(p => ({ ...p, store: e.target.value }))} placeholder="Business" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
        Save Preferences
      </Button>
    </div>
  );
}
